import React from "react";

import { Button, InfoMessage } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useMutation } from "@tanstack/react-query";

import UserService from "@/services/UserService";

import { useAuth } from "@/hooks/useAuth";

import { promiseToast } from "@/modules/index";

/**
 * One line above the page until the address is confirmed: the account
 * works meanwhile, but invites nobody and buys nothing (the API refuses
 * both). Not dismissible - it goes when the link is followed.
 */
export const EmailVerificationBanner: React.FC = () => {
	const { user } = useAuth();
	const resend = useMutation({
		mutationFn: () => UserService.resendEmailVerification(),
	});

	// `undefined` (an older /me): say nothing rather than nag wrongly.
	if (!user || user.emailVerified !== false) return null;

	return (
		<InfoMessage
			color="info"
			size="sm"
			className="mx-4 mt-4"
			data-testid="email-verification-banner"
		>
			<span className="flex flex-wrap items-center gap-x-2 gap-y-1">
				<span>
					{t("account.verify.banner", {
						email: user.mail,
						// Inviting collaborators is only said while sharing is on.
						context: user.features?.sharing ? "sharing" : undefined,
					})}
				</span>
				<Button
					color="info"
					variant="link"
					size="sm"
					isLoading={resend.isPending}
					disabled={resend.isSuccess}
					onClick={() =>
						promiseToast(resend.mutateAsync(), {
							success: t("account.verify.resent", {
								email: user.mail,
							}),
						})
					}
				>
					{t("account.verify.resend")}
				</Button>
			</span>
		</InfoMessage>
	);
};
